import { CheckCircle2, XCircle, FileText, Clock, Download } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { SUB_ACCOUNTS, formatTRY, formatDate } from "@/lib/constants";
import { casePdfUrl } from "@/lib/api";

export default function ReportingPanel({ caseData, docs, payments }) {
  const lehe = docs.filter((d) => d.sub_account === "sonuc_lehe");
  const aleyhe = docs.filter((d) => d.sub_account === "sonuc_aleyhe");

  const result = lehe.length > 0 ? "basarili" : aleyhe.length > 0 ? "basarisiz" : "derdest";
  const docsBySub = {};
  for (const d of docs) {
    docsBySub[d.sub_account] = (docsBySub[d.sub_account] || 0) + 1;
  }
  const total = payments.reduce((s, p) => s + (p.amount || 0), 0);

  return (
    <div className="space-y-6" data-testid="reporting-panel">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <FileText className="w-5 h-5 text-amber-500" />
          <div className="font-serif text-xl text-slate-100">Dava Raporu</div>
        </div>
        <a href={casePdfUrl(caseData.id)} target="_blank" rel="noreferrer" data-testid="download-pdf-link">
          <Button size="sm" className="bg-amber-600 hover:bg-amber-700 text-slate-950">
            <Download className="w-4 h-4 mr-1" /> PDF İndir
          </Button>
        </a>
      </div>

      {/* Verdict */}
      <Card className={
        result === "basarili" ? "bg-emerald-950/40 border-emerald-700/60"
        : result === "basarisiz" ? "bg-red-950/40 border-red-800/60"
        : "bg-slate-900/60 border-slate-700"
      }>
        <CardContent className="p-6 flex items-center gap-4">
          {result === "basarili" && <CheckCircle2 className="w-12 h-12 text-emerald-500" />}
          {result === "basarisiz" && <XCircle className="w-12 h-12 text-red-500" />}
          {result === "derdest" && <Clock className="w-12 h-12 text-slate-500" />}
          <div>
            <div className="text-[11px] font-mono uppercase tracking-widest text-slate-400">Nihai Durum</div>
            <div className={`text-3xl font-serif font-bold ${
              result === "basarili" ? "text-emerald-400" :
              result === "basarisiz" ? "text-red-400" : "text-slate-300"
            }`} data-testid="verdict-label">
              {result === "basarili" && "BAŞARILI (+)"}
              {result === "basarisiz" && "BAŞARISIZ (-)"}
              {result === "derdest" && "DERDEST"}
            </div>
            <div className="text-xs text-slate-500 mt-1">
              {result === "basarili" && "Nihai Lehe Karar belgesi mevcut"}
              {result === "basarisiz" && "Nihai Aleyhe Karar belgesi mevcut"}
              {result === "derdest" && "Sonuç belgesi henüz yüklenmemiş"}
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid md:grid-cols-2 gap-5">
        <Card className="bg-slate-900/60 border-slate-800">
          <CardContent className="p-5">
            <div className="text-[11px] font-mono uppercase tracking-widest text-slate-400 mb-3">Belge Dağılımı</div>
            <div className="space-y-2">
              {SUB_ACCOUNTS.filter((s) => s.key !== "raporlama").map((sa) => {
                const count = sa.key === "sonuc" ? (lehe.length + aleyhe.length) : (docsBySub[sa.key] || 0);
                return (
                  <div key={sa.key} className="flex justify-between items-center text-sm">
                    <span className="text-slate-300">{sa.label}</span>
                    <Badge variant="outline" className="border-slate-700 text-slate-300 font-mono">{count}</Badge>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        <Card className="bg-slate-900/60 border-slate-800">
          <CardContent className="p-5">
            <div className="text-[11px] font-mono uppercase tracking-widest text-slate-400 mb-3">Finansal Özet</div>
            <div className="space-y-2 text-sm">
              <Row k="Dava No" v={<span className="font-mono">{caseData.dava_no}</span>} />
              <Row k="Mahkeme" v={caseData.mahkeme_adi} />
              <Row k="Müvekkil" v={caseData.muvekkil || "—"} />
              <Row k="İş Bedeli" v={<span className="text-amber-500 font-semibold">{formatTRY(caseData.is_bedeli)}</span>} />
              <Row k="Tahsilat" v={<span className="text-emerald-400 font-semibold">{formatTRY(total)}</span>} />
              <Row k="Kalan" v={<span className="text-red-400 font-semibold">{formatTRY((caseData.is_bedeli || 0) - total)}</span>} />
              <Row k="Oluşturma" v={formatDate(caseData.created_at)} />
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function Row({ k, v }) {
  return (
    <div className="flex justify-between gap-4 py-1 border-b border-slate-800/60 last:border-0">
      <span className="text-slate-400">{k}</span>
      <span className="text-slate-200 text-right">{v}</span>
    </div>
  );
}
